import html2canvas from "html2canvas";
import { getT } from "../../../locales";

/**
 * Element içindeki tüm resimlerin yüklenmesini bekler
 * @param element - Resimleri beklenecek HTML elementi
 * @param timeout - Maksimum bekleme süresi (ms), default 5000
 */
async function waitForImages(
  element: HTMLElement,
  timeout: number = 5000
): Promise<void> {
  const images = element.querySelectorAll("img");
  const promises = Array.from(images).map((img) => {
    // Resim zaten yüklüyse devam et
    if (img.complete && img.naturalHeight !== 0) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const timeoutId = setTimeout(() => {
        resolve();
      }, timeout);

      img.onload = () => {
        clearTimeout(timeoutId);
        resolve();
      };
      img.onerror = () => {
        clearTimeout(timeoutId);
        resolve();
      };
    });
  });
  await Promise.all(promises);
}

/**
 * Legacy MathLive math-field elementlerinin child text node'larını geçici olarak gizler
 * html2canvas bu text node'ları yakalayınca LaTeX kodu görünür hale geliyor
 * @param element - İşlenecek HTML elementi
 * @returns Geri yüklemek için kullanılacak map
 */
function hideMathFieldTextNodes(
  element: HTMLElement
): Map<Element, ChildNode[]> {
  const mathFields = element.querySelectorAll("math-field");
  const originalContents: Map<Element, ChildNode[]> = new Map();

  mathFields.forEach((mf) => {
    const childNodes = Array.from(mf.childNodes);
    // Sadece text node'ları filtrele (nodeType 3 = TEXT_NODE)
    const textNodes = childNodes.filter((n) => n.nodeType === Node.TEXT_NODE);
    if (textNodes.length > 0) {
      // Orijinal node'ları sakla
      originalContents.set(mf, textNodes.map((n) => n.cloneNode(true) as ChildNode));
      // Text node'ları kaldır
      textNodes.forEach((tn) => tn.remove());
    }
  });

  return originalContents;
}

/**
 * Gizlenen legacy MathLive text node'larını geri yükler
 * @param originalContents - hideMathFieldTextNodes'dan dönen map
 */
function restoreMathFieldTextNodes(
  originalContents: Map<Element, ChildNode[]>
): void {
  originalContents.forEach((nodes, mf) => {
    nodes.forEach((n) => mf.appendChild(n));
  });
}

/**
 * Element içindeki resimleri fetch edip Base64 Data URL'e çevirir
 * Bu işlem html2canvas'ın CORS sorunlarını aşmasını sağlar
 * @param element - İşlenecek HTML elementi
 * @returns Orijinal src'leri içeren map
 */
async function convertImagesToDataURLs(
  element: HTMLElement
): Promise<Map<HTMLImageElement, string>> {
  const images = element.querySelectorAll("img");
  const originalSrcs = new Map<HTMLImageElement, string>();

  const promises = Array.from(images).map(async (img) => {
    try {
      // Data URL veya boş src ise atla
      if (!img.src || img.src.startsWith("data:")) return;

      // Resmi fetch et
      const response = await fetch(img.src, { mode: "cors" });
      const blob = await response.blob();
      
      return new Promise<void>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            originalSrcs.set(img, img.src);
            img.src = reader.result as string;
          }
          resolve();
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn("Image conversion error:", img.src, error);
      // On error, keep original src and let html2canvas try
    }
  });

  await Promise.all(promises);
  return originalSrcs;
}

/**
 * Değiştirilen resim src'lerini geri yükler
 * @param originalSrcs - convertImagesToDataURLs'den dönen map
 */
function restoreOriginalImages(
  originalSrcs: Map<HTMLImageElement, string>
): void {
  originalSrcs.forEach((src, img) => {
    img.src = src;
  });
}

function hasMathML(element: HTMLElement): boolean {
  return element.querySelector("math") !== null;
}

function resolveScreenshotElement(element: HTMLElement): HTMLElement {
  if (element.classList.contains("qe-canvas")) {
    return element;
  }
  const canvas = element.querySelector(".qe-canvas") as HTMLElement | null;
  return canvas || element;
}

function createSnapshotWrapper(
  element: HTMLElement
): { target: HTMLElement; cleanup: () => void } {
  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.left = "-100000px";
  wrapper.style.top = "0";
  wrapper.style.opacity = "0";
  wrapper.style.pointerEvents = "none";
  wrapper.style.zIndex = "-1";
  wrapper.style.overflow = "visible";

  const clone = element.cloneNode(true) as HTMLElement;
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  return {
    target: clone,
    cleanup: () => {
      wrapper.remove();
    },
  };
}

const WIRIS_CREATE_IMAGE_URL = "https://www.wiris.net/demo/plugins/app/createimage";

async function fetchWirisMathImage(mathml: string): Promise<string | null> {
  try {
    const body = new URLSearchParams({
      mml: mathml,
      base64: "true",
      metrics: "true",
    });

    const response = await fetch(WIRIS_CREATE_IMAGE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    });

    if (!response.ok) {
      return null;
    }

    const dataUrl = (await response.text()).trim();
    if (!dataUrl.startsWith("data:image/")) {
      return null;
    }
    return dataUrl;
  } catch (error) {
    console.warn("MathML image fetch error:", error);
    return null;
  }
}

async function replaceMathMLWithServiceImages(
  element: HTMLElement
): Promise<void> {
  const mathElements = Array.from(element.querySelectorAll("math"));
  if (mathElements.length === 0) {
    return;
  }

  const cache = new Map<string, string>();
  await Promise.all(
    mathElements.map(async (mathEl) => {
      const mathml = mathEl.outerHTML;
      const cached = cache.get(mathml);
      if (cached) {
        const img = document.createElement("img");
        img.src = cached;
        img.alt = "Math formula";
        img.style.display = "inline-block";
        img.style.verticalAlign = "middle";
        img.style.maxWidth = "100%";
        img.crossOrigin = "anonymous";
        mathEl.replaceWith(img);
        return;
      }

      const fetched = await fetchWirisMathImage(mathml);
      if (!fetched) {
        return;
      }
      cache.set(mathml, fetched);

      const img = document.createElement("img");
      img.src = fetched;
      img.alt = "Math formula";
      img.style.display = "inline-block";
      img.style.verticalAlign = "middle";
      img.style.maxWidth = "100%";
      img.crossOrigin = "anonymous";
      mathEl.replaceWith(img);
    })
  );
}

function getSnapshotSize(element: HTMLElement): { width: number; height: number } {
  const width = Math.ceil(Math.max(element.scrollWidth, element.offsetWidth));
  const height = Math.ceil(Math.max(element.scrollHeight, element.offsetHeight));
  return { width, height };
}

function relaxCanvasOverflow(clonedDoc: Document): void {
  const selectors = [".qe-body", ".qe-stage", ".qe-canvas-shell"];
  selectors.forEach((selector) => {
    const el = clonedDoc.querySelector(selector) as HTMLElement | null;
    if (!el) return;
    el.style.overflow = "visible";
    el.style.maxHeight = "none";
    el.style.maxWidth = "none";
    el.style.height = "auto";
    el.style.width = "auto";
  });

  const html = clonedDoc.documentElement;
  html.style.overflow = "visible";
  html.style.height = "auto";
  html.style.width = "auto";

  const body = clonedDoc.body;
  body.style.overflow = "visible";
  body.style.height = "auto";
  body.style.width = "auto";
}

type WirisPlugin = {
  Parser?: {
    mathmlToImgObject: (
      creator: Document,
      mathml: string,
      wirisProperties: unknown,
      language: unknown
    ) => HTMLImageElement | null;
  };
  Configuration?: {
    get: (key: string) => unknown;
    set: (key: string, value: unknown) => void;
  };
};

function replaceMathMLWithWirisImages(clonedDoc: Document): void {
  const wiris = (window as any).WirisPlugin as WirisPlugin | undefined;
  if (!wiris?.Parser?.mathmlToImgObject || !wiris?.Configuration) {
    return;
  }

  const mathElements = Array.from(clonedDoc.querySelectorAll("math"));
  if (mathElements.length === 0) {
    return;
  }

  const config = wiris.Configuration;
  const prevSaveMode = config.get("saveMode");
  const prevBase64SaveMode = config.get("base64savemode");

  if (prevSaveMode !== "base64") {
    config.set("saveMode", "base64");
  }
  if (prevBase64SaveMode !== "default") {
    config.set("base64savemode", "default");
  }

  mathElements.forEach((mathEl) => {
    try {
      const mathml = mathEl.outerHTML;
      const img = wiris.Parser!.mathmlToImgObject(
        clonedDoc,
        mathml,
        null,
        null
      );
      if (!img) return;
      img.crossOrigin = "anonymous";
      img.style.display = "inline-block";
      img.style.verticalAlign = "middle";
      mathEl.replaceWith(img);
    } catch (error) {
      console.warn("MathML image render error:", error);
    }
  });

  if (prevSaveMode !== "base64") {
    config.set("saveMode", prevSaveMode);
  }
  if (prevBase64SaveMode !== "default") {
    config.set("base64savemode", prevBase64SaveMode);
  }
}

/**
 * Canvas elementinin ekran görüntüsünü alır ve base64 string olarak döndürür
 * @param canvasElement - Canvas DOM elementi (ref.current)
 * @param options - Screenshot seçenekleri
 * @returns Base64 string (data:image/png;base64,...) veya null
 */
export async function captureCanvasScreenshot(
  canvasElement: HTMLElement | null,
  options: {
    quality?: number; // 0-1 arası, default 0.92
    format?: "png" | "jpeg";
    scale?: number; // Çözünürlük çarpanı, default 1
  } = {}
): Promise<string | null> {
  if (!canvasElement) {
    const t = getT();
    console.warn(t("canvasScreenshot.error.canvasNotFound"));
    return null;
  }

  const resolvedElement = resolveScreenshotElement(canvasElement);
  const snapshot = createSnapshotWrapper(resolvedElement);
  const snapshotTarget = snapshot.target;

  // Legacy MathLive text node'larını sakla
  let originalMathFieldContents: Map<Element, ChildNode[]> = new Map();
  // Orijinal resim src'lerini sakla
  let originalImageSrcs: Map<HTMLImageElement, string> = new Map();

  try {
    const { quality = 0.92, format = "png", scale = 1 } = options;
    const { width: snapshotWidth, height: snapshotHeight } =
      getSnapshotSize(snapshotTarget);

    // 1. Resimlerin yüklenmesini bekle
    await waitForImages(snapshotTarget);

    // 2. Resimleri Data URL'e çevir (CORS bypass)
    originalImageSrcs = await convertImagesToDataURLs(snapshotTarget);

    // 3. MathML'leri resme çevir (MathType görünümü için)
    await replaceMathMLWithServiceImages(snapshotTarget);

    // 4. Legacy MathLive elementlerinin child text node'larını geçici olarak gizle
    // Bu, html2canvas'ın LaTeX kodunu yakalamasını önler
    originalMathFieldContents = hideMathFieldTextNodes(snapshotTarget);

    // 5. MathML resimlerinin yüklenmesini bekle
    await waitForImages(snapshotTarget);

    // 6. Screenshot al
    const canvas = await html2canvas(snapshotTarget, {
      backgroundColor: null, // Transparent background
      scale: scale,
      logging: true,
      useCORS: true,
      foreignObjectRendering: hasMathML(snapshotTarget),
      // allowTaint: false - CORS hatalarında tainted canvas hatası almamak için kapalı tutuyoruz
      allowTaint: false,
      width: snapshotWidth,
      height: snapshotHeight,
      windowWidth: snapshotWidth,
      windowHeight: snapshotHeight,
      // Klonlanan DOM'da düzenleyici UI elementlerini gizle (orijinal DOM'u etkilemez)
      onclone: (clonedDoc) => {
        relaxCanvasOverflow(clonedDoc);
        replaceMathMLWithWirisImages(clonedDoc);

        // Resimlere crossOrigin ekle - Data URL'e çevrilmiş olsa bile güvenlik için
        const images = clonedDoc.querySelectorAll("img");
        images.forEach((img) => {
          img.crossOrigin = "anonymous";
        });
        
        // Resize handle'ları gizle
        const resizeHandles = clonedDoc.querySelectorAll(".qe-resize-handle");
        resizeHandles.forEach((handle) => {
          (handle as HTMLElement).style.display = "none";
        });
        
        // Seçili element border'ını kaldır
        const selectedElements = clonedDoc.querySelectorAll(".qe-canvas__element--selected");
        selectedElements.forEach((el) => {
          (el as HTMLElement).style.border = "none";
          (el as HTMLElement).style.backgroundColor = "transparent";
        });
        
        // Element handle'ları gizle (element türü etiketi)
        const elementHandles = clonedDoc.querySelectorAll(".qe-element__handle");
        elementHandles.forEach((handle) => {
          (handle as HTMLElement).style.display = "none";
        });
      },
    });

    // Canvas'ı base64 string'e çevir
    const base64String = canvas.toDataURL(`image/${format}`, quality);
    return base64String;
  } catch (error) {
    console.error("Canvas screenshot error:", error);
    return null;
  } finally {
    // 5. Gizlenen text node'ları geri yükle
    restoreMathFieldTextNodes(originalMathFieldContents);
    
    // 6. Orijinal resim src'lerini geri yükle
    restoreOriginalImages(originalImageSrcs);
    snapshot.cleanup();
  }
}

/**
 * Preview container'ından screenshot alır
 * Bu fonksiyon, canvas elementinden değil preview modunda render edilmiş
 * bir offscreen container'dan screenshot alır.
 * @param previewContainer - Preview modunda render edilmiş HTML elementi
 * @param options - Screenshot seçenekleri
 * @returns Base64 string veya null
 */
export async function capturePreviewScreenshot(
  previewContainer: HTMLElement | null,
  options: {
    quality?: number;
    format?: "png" | "jpeg";
    scale?: number;
  } = {}
): Promise<string | null> {
  if (!previewContainer) {
    const t = getT();
    console.warn(t("canvasScreenshot.error.previewNotFound"));
    return null;
  }

  // Legacy MathLive text node'larını sakla
  let originalMathFieldContents: Map<Element, ChildNode[]> = new Map();

  try {
    const { quality = 0.92, format = "png", scale = 1 } = options;
    const { width: snapshotWidth, height: snapshotHeight } =
      getSnapshotSize(previewContainer);

    // 1. Resimlerin yüklenmesini bekle (preview modunda resimler yeniden yüklenebilir)
    await waitForImages(previewContainer, 8000);

    // 2. Kısa bir bekleme ekle - React render'ının tamamlanmasını bekle
    await new Promise((resolve) => setTimeout(resolve, 500));

    // 3. MathML'leri resme çevir (MathType görünümü için)
    await replaceMathMLWithServiceImages(previewContainer);

    // 4. Legacy MathLive elementlerinin child text node'larını geçici olarak gizle
    originalMathFieldContents = hideMathFieldTextNodes(previewContainer);

    // 5. MathML resimlerinin yüklenmesini bekle
    await waitForImages(previewContainer, 8000);

    // 6. Screenshot al
    const canvas = await html2canvas(previewContainer, {
      backgroundColor: null,
      scale: scale,
      logging: false,
      useCORS: true,
      foreignObjectRendering: hasMathML(previewContainer),
      allowTaint: false,
      width: snapshotWidth,
      height: snapshotHeight,
      windowWidth: snapshotWidth,
      windowHeight: snapshotHeight,
      // Preview container için ek ayarlar
      imageTimeout: 10000, // Resimlerin yüklenmesi için daha uzun timeout
      removeContainer: false,
      onclone: (clonedDoc) => {
        relaxCanvasOverflow(clonedDoc);
        replaceMathMLWithWirisImages(clonedDoc);
      },
    });

    const base64String = canvas.toDataURL(`image/${format}`, quality);
    return base64String;
  } catch (error) {
    console.error("Preview screenshot error:", error);
    return null;
  } finally {
    restoreMathFieldTextNodes(originalMathFieldContents);
  }
}

/**
 * Base64 string'i File objesine çevirir
 * @param base64String - Base64 data URL string
 * @param filename - Dosya adı
 * @returns File objesi veya null
 */
export function base64ToFile(
  base64String: string,
  filename: string = "canvas-screenshot.png"
): File | null {
  try {
    // Base64 string'den data ve mime type'ı ayır
    const arr = base64String.split(",");
    if (arr.length < 2) {
      const t = getT();
      console.error(t("canvasScreenshot.error.invalidBase64"));
      return null;
    }

    const mimeMatch = arr[0].match(/:(.*?);/);
    const mimeType = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mimeType });
  } catch (error) {
    console.error("Base64 to File conversion error:", error);
    return null;
  }
}

/**
 * Canvas screenshot'ını hem base64 hem de File formatında döndürür
 * @param canvasElement - Canvas DOM elementi
 * @param options - Screenshot seçenekleri
 * @returns { base64: string | null, file: File | null }
 */
export async function captureCanvasScreenshotBothFormats(
  canvasElement: HTMLElement | null,
  options: {
    quality?: number;
    format?: "png" | "jpeg";
    scale?: number;
    filename?: string;
  } = {}
): Promise<{ base64: string | null; file: File | null }> {
  const base64 = await captureCanvasScreenshot(canvasElement, options);

  if (!base64) {
    return { base64: null, file: null };
  }

  const filename =
    options.filename ||
    `canvas-screenshot-${Date.now()}.${options.format || "png"}`;
  const file = base64ToFile(base64, filename);

  return { base64, file };
}
